import { For, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import theme from '../../style/theme';
import { Slider } from '../../ui-components/slider/slider';
import { Tabnav, TabnavItem } from '../../ui-components/tabnav/tabnav';
import { Pagination } from '../../ui-components/pagination/pagination';
import { ProductCard } from '../products/product-card/product-card';
import { SLIDE_STRETCH } from '../card-slider/slider-card-media';
import { SearchResultRow } from './search-result-row';
import { mapHitToProductCard, SEARCH_RESULT_FILTERS, SearchHit, SearchResultFilter } from './search-shared';

export type SearchResultsProps = {
    query: string;
    filter: SearchResultFilter;
    /** 1-based. */
    page: number;
    hitsPerPage: number;
    hits: SearchHit[];
    totalHits: number;
    totalPages: number;
    /** Hits per content type for the whole query, independent of the active filter. */
    counts: Partial<Record<SearchResultFilter, number>>;
    /** Product hits shown as cards above the list when every type is listed. */
    productHits: SearchHit[];
    loading: boolean;
    onFilterChange: (filter: SearchResultFilter) => void;
    onPageChange: (page: number) => void;
};

/* Product card gap, 24px at the 11px root. The slider takes rem. */
const PRODUCT_SLIDE_GAP_REM = 24 / 11;

export const SEARCH_RESULTS_ANCHOR = 'search-results';
/* The tabs row: where paging scrolls to, so the product row above stays out of the way. */
export const SEARCH_RESULTS_LIST_ANCHOR = 'search-results-list';

/* The results half of the search page: the product card row, the content-type tabs, the
   result rows and the pagination (Figma 1602:11181 / 1436:10395). Presentational — the
   page owns the Algolia calls and the URL. */
export const SearchResults = (props: SearchResultsProps) => {
    const { localize, viewport } = useContext(AppContext);

    const filterLabel = (filter: SearchResultFilter) => {
        switch (filter) {
            case 'all':
                return localize('search-all', 'All');
            case 'page':
                return localize('pages', 'Pages');
            case 'product':
                return localize('products', 'Products');
            case 'blog-and-news':
                return localize('blog-and-news', 'Blog & News');
            case 'event':
                return localize('events', 'Events');
            case 'document':
                return localize('documents', 'Documents');
            case 'video-item':
                return localize('videos', 'Videos');
        }
    };

    const tabs = (): TabnavItem[] => SEARCH_RESULT_FILTERS.map((filter) => {
        const count = props.counts[filter] ?? 0;

        return {
            key: filter,
            label: `${filterLabel(filter)} (${count})`,
            disabled: filter !== 'all' && count === 0,
        };
    });

    /* The product row only makes sense on the unfiltered list; the product tab lists the
       same hits as rows. Hidden outright when the query matched no products. */
    const showProductRow = () => props.filter === 'all' && props.productHits.length > 0;

    const productsPerView = () => {
        if (viewport.width > theme.breakpoints.DESKTOP) return 4;
        if (viewport.width > theme.breakpoints.DESKTOP_SMALL) return 3;
        if (viewport.width > theme.breakpoints.TABLET) return 2;
        return 1;
    };

    const summary = () => {
        if (props.totalHits === 0) {
            return '';
        }

        const from = (props.page - 1) * props.hitsPerPage + 1;
        const to = Math.min(props.page * props.hitsPerPage, props.totalHits);

        return `${from} - ${to} ${localize('of', 'of')} ${props.totalHits} ${localize('results', 'results')}`;
    };

    return (
        <Section
            templateShorthand={[12]}
            widthType="bgFull"
            removeSpacingBlock={true}
            customCss="padding-top: 0 !important; padding-bottom: 0 !important;"
        >
            <div id={SEARCH_RESULTS_ANCHOR} class="pb-cvi-section-spacious pt-cvi-11">
                {/* Keyed on the hit array: keen-slider measures its slides once, when the
                    Slider mounts, so the row has to mount with its cards already in place
                    and remount when a new query brings a different set. */}
                <Show when={showProductRow() && props.productHits} keyed>
                    {(hits) => (
                        <section aria-labelledby="search-results-products-heading">
                            <h2 id="search-results-products-heading" class="m-0 pb-cvi-8 heading-2xl font-cvi-regular text-cvi-fg-default">
                                {localize('products', 'Products')}
                            </h2>
                            <Slider slidesPerView={productsPerView()} spacing={PRODUCT_SLIDE_GAP_REM} slideHeight="auto" allowSlideShadow>
                                <For each={hits}>
                                    {(hit) => (
                                        <div class={SLIDE_STRETCH}>
                                            <ProductCard data={mapHitToProductCard(hit)} inGrouping={true} />
                                        </div>
                                    )}
                                </For>
                            </Slider>
                            <div class="mt-cvi-8 h-px bg-cvi-border-muted" aria-hidden="true" />
                        </section>
                    )}
                </Show>

                <div id={SEARCH_RESULTS_LIST_ANCHOR} class="pt-cvi-10">
                    <Tabnav
                        tabs={tabs()}
                        activeKey={props.filter}
                        onChange={(key) => props.onFilterChange(key as SearchResultFilter)}
                        ariaLabel={localize('search-result-types', 'Result types')}
                        overflowAriaLabel={localize('more', 'More')}
                    />
                </div>

                <div class="pt-cvi-10" aria-busy={props.loading}>
                    <Show when={props.query && !props.loading && props.hits.length === 0}>
                        <p class="m-0 body-md font-cvi-regular text-cvi-fg-muted">
                            {localize('no-results', 'No results...')}
                        </p>
                    </Show>

                    <ul class="m-0 list-none p-0">
                        <For each={props.hits}>
                            {(hit) => (
                                <li class="border-b border-cvi-border-muted py-cvi-8 first:pt-0">
                                    <SearchResultRow hit={hit} />
                                </li>
                            )}
                        </For>
                    </ul>

                    <Pagination
                        page={props.page}
                        totalPages={props.totalPages}
                        onChange={props.onPageChange}
                        summary={summary()}
                        ariaLabel={localize('search-results', 'Search results')}
                    />
                </div>
            </div>
        </Section>
    );
};
